// The real-satellite catalogue: CelesTrak's GP element sets (OMM) cut down
// to what SGP4 needs plus a name, the NORAD id and a display group. Shared
// by the API that serves it fresh (api/satellites.js), the script that
// writes the fallback snapshot (scripts/build-satellite-snapshot.mjs) and
// the viewer's worker (src/satellites/worker.js), so all three agree on the
// row layout. Pure ESM: no DOM, safe in Node.
//
// A row: [norad, group, name, epochMs, meanMotion (rev/day), eccentricity,
//         inclination, raan, argOfPericenter, meanAnomaly (deg), bstar,
//         meanMotionDot]

export const SAT_GROUPS = [
  { key: 'starlink', label: 'Starlink', color: 0xe6edf7 },
  { key: 'oneweb', label: 'OneWeb', color: 0x8fc8ff },
  { key: 'kuiper', label: 'Kuiper', color: 0xffb86b },
  { key: 'china', label: 'Qianfan, Guowang', color: 0xff9090 },
  { key: 'gnss', label: 'Navigation', color: 0x7dffb0 },
  { key: 'stations', label: 'Stations', color: 0xffffff },
  { key: 'earthsci', label: 'Weather, science', color: 0x6fe3ff },
  { key: 'geo', label: 'Geostationary', color: 0xffd36b },
  { key: 'other', label: 'Other', color: 0xb4b4c2 },
];

export const ROW_FIELDS = ['norad', 'group', 'name', 'epochMs', 'n', 'e', 'i', 'raan', 'argp', 'ma', 'bstar', 'ndot'];

// CelesTrak groups fetched per refresh: 'active' is every working
// satellite; the rest only sort them into display groups.
export const CELESTRAK_GROUPS = ['active', 'stations', 'gnss', 'weather', 'science', 'geo'];

export function celestrakUrl(group) {
  return `https://celestrak.org/NORAD/elements/gp.php?GROUP=${encodeURIComponent(group)}&FORMAT=json`;
}

const GROUP_INDEX = Object.fromEntries(SAT_GROUPS.map((g, i) => [g.key, i]));

function classify(o, sets) {
  const name = String(o.OBJECT_NAME || '').toUpperCase();
  const id = o.NORAD_CAT_ID;
  if (sets.stations.has(id)) return 'stations';
  if (name.startsWith('STARLINK')) return 'starlink';
  if (name.startsWith('ONEWEB')) return 'oneweb';
  if (name.startsWith('KUIPER')) return 'kuiper';
  if (/^(QIANFAN|GUOWANG|HULIANWANG)/.test(name)) return 'china';
  if (sets.gnss.has(id)) return 'gnss';
  if (sets.weather.has(id) || sets.science.has(id)) return 'earthsci';
  if (sets.geo.has(id)) return 'geo';
  return 'other';
}

const fixed = (v, d) => Number(Number(v).toFixed(d));
const sig = (v, d) => Number(Number(v).toPrecision(d));

// lists: { active: [omm...], stations: [...], gnss: [...], weather, science, geo }
export function buildCatalog(lists, { generated = new Date().toISOString() } = {}) {
  const ids = (k) => new Set((lists[k] || []).map((o) => o.NORAD_CAT_ID));
  const sets = { stations: ids('stations'), gnss: ids('gnss'), weather: ids('weather'), science: ids('science'), geo: ids('geo') };
  const seen = new Set();
  const sats = [];
  for (const o of lists.active || []) {
    const id = o.NORAD_CAT_ID;
    if (seen.has(id) || !(o.MEAN_MOTION > 0)) continue;
    seen.add(id);
    // CelesTrak writes EPOCH in UTC without a zone.
    const epochMs = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(o.EPOCH) ? o.EPOCH : `${o.EPOCH}Z`);
    if (!Number.isFinite(epochMs)) continue;
    sats.push([
      id, GROUP_INDEX[classify(o, sets)], String(o.OBJECT_NAME || id),
      epochMs, fixed(o.MEAN_MOTION, 8), fixed(o.ECCENTRICITY, 7), fixed(o.INCLINATION, 4),
      fixed(o.RA_OF_ASC_NODE, 4), fixed(o.ARG_OF_PERICENTER, 4), fixed(o.MEAN_ANOMALY, 4),
      sig(o.BSTAR || 0, 5), sig(o.MEAN_MOTION_DOT || 0, 5),
    ]);
  }
  return { v: 1, generated, source: 'CelesTrak GP (celestrak.org)', groups: SAT_GROUPS.map((g) => g.key), fields: ROW_FIELDS, sats };
}

// The OMM object satellite.js's json2satrec reads, rebuilt from a row.
export function rowToOmm(r) {
  return {
    OBJECT_NAME: r[2],
    NORAD_CAT_ID: r[0],
    EPOCH: new Date(r[3]).toISOString().replace('Z', ''),
    MEAN_MOTION: r[4],
    ECCENTRICITY: r[5],
    INCLINATION: r[6],
    RA_OF_ASC_NODE: r[7],
    ARG_OF_PERICENTER: r[8],
    MEAN_ANOMALY: r[9],
    BSTAR: r[10],
    MEAN_MOTION_DOT: r[11],
    MEAN_MOTION_DDOT: 0,
    EPHEMERIS_TYPE: 0,
    CLASSIFICATION_TYPE: 'U',
    ELEMENT_SET_NO: 999,
    REV_AT_EPOCH: 0,
  };
}
